import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/utils/axios";
import { formatDateTime } from "@/utils/formatDateTime";
import avatarFallback from "@/assets/UI/avatar.png";
import walletIcon from "@/assets/UI/wallet.webp";
import s from "./PromoRedemptions.module.scss";

const DEFAULT_LIMIT = 50;

const PromoRedemptions = () => {
  const [redemptions, setRedemptions] = useState([]);
  const [filters, setFilters] = useState({ promoCode: "", username: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRedemptions = useCallback(async (activeFilters) => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/v1/admin/finance/promo-redemptions", {
        params: {
          limit: DEFAULT_LIMIT,
          promoCode: activeFilters.promoCode || undefined,
          username: activeFilters.username || undefined,
        },
      });
      setRedemptions(res.data?.data ?? []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load promo redemptions",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRedemptions({ promoCode: "", username: "" });
  }, [fetchRedemptions]);

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    fetchRedemptions(filters);
  };

  const resetFilters = () => {
    setFilters({ promoCode: "", username: "" });
  };

  const renderPrettyDateTime = (value) => {
    const dt = formatDateTime(value);
    if (!dt) return <span className={s.dash}>-</span>;

    const hasParts =
      dt.day !== undefined && dt.suffix && dt.month && dt.year !== undefined;

    return (
      <div className={s.dateTime}>
        <div className={s.date}>
          {hasParts ? (
            <>
              {dt.day}
              <sup className={s.sup}>{dt.suffix}</sup> {dt.month} {dt.year}
            </>
          ) : (
            dt.date
          )}
        </div>
        <div className={s.time}>{dt.time}</div>
      </div>
    );
  };

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerLeft}>
          <p className={s.eyebrow}>Finance • Promo redemptions</p>
          <h1 className={s.title}>Promo redemptions</h1>
          <p className={s.subtitle}>
            Review every promo redemption and link back to wallet transactions.
          </p>
        </div>
      </header>

      <section className={s.card}>
        <h2 className={s.sectionTitle}>Filters</h2>
        <form className={s.filters} onSubmit={applyFilters}>
          <label className={s.formRow}>
            <span className={s.label}>Promo code</span>
            <input
              className={s.input}
              value={filters.promoCode}
              onChange={handleChange("promoCode")}
              placeholder="Promo code"
            />
          </label>
          <label className={s.formRow}>
            <span className={s.label}>Username</span>
            <input
              className={s.input}
              value={filters.username}
              onChange={handleChange("username")}
              placeholder="Username"
            />
          </label>
          <div className={s.inlineActions}>
            <button className={`${s.btn} ${s.btnPrimary}`} type="submit">
              Apply
            </button>
            <button
              className={`${s.btn} ${s.btnSecondary}`}
              type="button"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
        </form>
      </section>

      {error && (
        <div className={`${s.stateBox} ${s.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      )}

      <section className={s.card}>
        <h2 className={s.sectionTitle}>Redemptions</h2>
        {loading ? (
          <div className={s.stateBox}>Loading redemptions…</div>
        ) : redemptions.length === 0 ? (
          <div className={s.stateBox}>No redemptions found.</div>
        ) : (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Promo</th>
                  <th>Redeemed</th>
                  <th className={s.thActions}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((entry) => {
                  const userId = entry.user?.id || entry.userId || null;
                  const memberPath = userId
                    ? `/admin/members/single/${userId}`
                    : null;
                  const walletPath = entry.walletId
                    ? `/admin/finance/wallets/${entry.walletId}`
                    : null;

                  return (
                    <tr key={entry.id} className={s.row}>
                      <td data-label="Member">
                        {memberPath ? (
                          <Link to={memberPath} className={s.memberLink}>
                            <span className={s.avatarRing}>
                              <img
                                src={entry.avatar?.url || avatarFallback}
                                alt=""
                                className={s.avatarLg}
                              />
                            </span>
                            <span className={s.memberText}>
                              <span className={s.userName}>
                                {entry.user?.username || "Unknown user"}
                              </span>
                            </span>
                          </Link>
                        ) : (
                          <div className={s.memberLink}>
                            <span className={s.avatarRing}>
                              <img
                                src={entry.avatar?.url || avatarFallback}
                                alt=""
                                className={s.avatarLg}
                              />
                            </span>
                            <span className={s.memberText}>
                              <span className={s.userName}>
                                {entry.user?.username || "Unknown user"}
                              </span>
                            </span>
                          </div>
                        )}
                      </td>
                      <td data-label="Promo">
                        <span className={s.codeText}>
                          {entry.promoCode?.code || "-"}
                        </span>
                      </td>

                      <td data-label="Redeemed" className={s.dateCell}>
                        {renderPrettyDateTime(entry.redeemedAt)}
                      </td>
                      <td data-label="Actions" className={s.actionsCell}>
                        <div className={s.actionStack}>
                          {walletPath ? (
                            <Link
                              className={`${s.iconBtn} ${s.iconBtnAlt}`}
                              to={walletPath}
                              title="View wallet"
                              aria-label="View wallet"
                            >
                              <img
                                className={s.iconBtnImg}
                                src={walletIcon}
                                alt=""
                              />
                            </Link>
                          ) : (
                            <span
                              className={`${s.iconBtn} ${s.iconBtnDisabled}`}
                              title="Wallet missing"
                              aria-label="Wallet missing"
                            >
                              <img
                                className={s.iconBtnImg}
                                src={walletIcon}
                                alt=""
                              />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default PromoRedemptions;
